<script lang="ts">
    import vm, { JourneyNode, JourneyStep } from '@journeys/Journeys.viewmodel';
    import { computed, reactive, ref } from 'vue';
    import NodeTemplate from '@journeys/components/NodePartials/NodeTemplate.vue';
    import Notification from '@shared/components/Alerts/Notification.vue';
    import PreviewNodeSegment from './PreviewNodeSegment.vue';

    // this is mostly for debugging, otherwise components are Anonymous
    const component = {
        name: 'StepCard',
    };

    export default {
        name: component.name,
    };
</script>

<script lang="ts" setup>
    const { account, editor, activeJourney } = vm;
    const showChain = ref(false);

    const props = defineProps<{
        step: JourneyStep;
        journeyIsDraft: boolean;
    }>();

    const state = reactive({
        showSegmentDefinition: false,
        showDev: false,
        loadingSegmentPreview: false,
    });

    const toggleChain = (chain: any) => {
        editor.tools.highlight = {};
        showChain.value = !showChain.value;

        if (showChain.value) {
            for (let link of chain.flat()) editor.tools.highlight[link] = activeJourney.nodes[link];
        }
    };

    const openSendLog = (issueId: string) => {
        editor.state.sendLogIssueFilter = issueId;
        editor.state.showSendLogModal = true;
    };

    const openSmsLog = (smsMessageId: string) => {
        editor.state.sendLogSmsFilter = smsMessageId;
        editor.state.showSendLogModal = true;
    };

    const stepListings = computed(() => {
        return {
            issue: account.listings.getListing('/issues/listing', props.step.data.IssueId || ''),
            survey: account.listings.getListing('/surveys/listing', props.step.data.SurveyId || ''),
            textmessage: account.listings.getListing('/textmessages/listing', props.step.data.SmsMessageId || ''),
            segment: account.listings.getListing('/segments', props.step.data.SegmentId),
        };
    });

    const timeSpanSince = computed(() => {
        return props.step.data.SegmentDefinition?.Segments?.find(
            (segment) => segment.TimeSpanSince && segment.IssueId === props.step.data.IssueId
        )?.TimeSpanSince?.split('.')[0];
    });

    const smsTimeSpanSince = computed(() => {
        return props.step.data.SegmentDefinition?.Segments?.find(
            (segment) => segment.TimeSpanSince && segment.SmsMessageId === props.step.data.SmsMessageId
        )?.TimeSpanSince?.split('.')[0];
    });

    (window as any).dev = vm;
</script>

<template>
    <div class="step-container">
        <h2 class="h5 m-0-t">
            <span @click="() => toggleChain(step.chain)" role="button">
                {{ $Resources.JourneyStepLabel }}:
                <span class="fw-400">{{ step.data.Name }}</span>
            </span>
        </h2>

        <div class="step-card d-flex gap-sm m-sm-b">
            <div class="step-icon">
                <NodeTemplate
                    :id="`icon${step.nodeId}`"
                    :icon="(activeJourney.nodes[step.nodeId] as JourneyNode).i"
                    :iconColor="{ h: 180, s: 100, l: 20 }"
                    :frameColor="{ h: 180, s: 70, l: 60 }"
                    style="transform: scale(1.4)" />
            </div>
            <div class="flex-1">
                <h3 class="h4 m-0-t" v-if="stepListings.issue">
                    <a :href="'/issues/' + stepListings.issue.IssueId" target="_blank" rel="noopener">
                        {{ stepListings.issue.Subject || $Resources.NoNamePlaceholder }}
                        <small class="glyphicon glyphicon glyphicon-new-window m-xs-l" aria-hidden="true"></small>
                    </a>
                </h3>

                <h3 class="h4 m-0-t" v-if="stepListings.survey">
                    <a :href="'/surveys/' + stepListings.survey.SurveyId" target="_blank" rel="noopener">
                        {{ stepListings.survey.Title || $Resources.NoNamePlaceholder }}
                        <small class="glyphicon glyphicon glyphicon-new-window m-xs-l" aria-hidden="true"></small>
                    </a>
                </h3>

                <h3 class="h4 m-0-t" v-if="stepListings.textmessage">
                    <a :href="'/textmessages/' + stepListings.textmessage.SmsMessageId" target="_blank" rel="noopener">
                        {{ stepListings.textmessage.Subject || $Resources.NoNamePlaceholder }}
                        <small class="glyphicon glyphicon glyphicon-new-window m-xs-l" aria-hidden="true"></small>
                    </a>
                </h3>
            </div>
        </div>

        <Notification
            v-if="
                !step.data.SmsMessageId &&
                step.data.IssueId &&
                step.data.SegmentDefinition?.Segments &&
                step.data.SegmentDefinition.Segments.length > 0 &&
                step.data.SegmentDefinition.Segments.find((segment) => segment.TimeSpanSince && segment.IssueId === step.data.IssueId)
            "
            type="warning"
            :message="{
                head: $Resources.NotifyIssueIsResendableLabel,
                body: $Resources.NotifyIssueIsResenableTimeInterval.replace('{{ days }}', timeSpanSince ?? 'error'),
            }"
            sizing="small"
            :inspect="undefined" />

        <Notification
            v-if="
                step.data.SmsMessageId &&
                !step.data.IssueId &&
                step.data.SegmentDefinition?.Segments &&
                step.data.SegmentDefinition.Segments.length > 0 &&
                step.data.SegmentDefinition.Segments.find((segment) => segment.TimeSpanSince && segment.SmsMessageId === step.data.SmsMessageId)
            "
            type="warning"
            :message="{
                head: $Resources.NotifySmsIsResendableLabel,
                body: $Resources.NotifySmsIsResenableTimeInterval.replace('{{ days }}', smsTimeSpanSince ?? 'error'),
            }"
            sizing="small"
            :inspect="undefined" />

        <PreviewNodeSegment v-if="step.data.SegmentDefinition" :segmentDefinition="step.data.SegmentDefinition" btnClass="btn-sm" />

        <div class="form-group m-sm-b" v-if="step.data.IssueId && !journeyIsDraft">
            <button class="btn btn-subtle btn-sm btn-block" @click="openSendLog(step.data.IssueId)">
                {{ $Resources.JourneySendLogOpenLogBtn }}
            </button>
        </div>

        <div class="form-group m-sm-b" v-if="step.data.SmsMessageId && !journeyIsDraft">
            <button class="btn btn-subtle btn-sm btn-block" @click="openSmsLog(step.data.SmsMessageId)">
                {{ $Resources.JourneySendLogOpenLogBtn }}
            </button>
        </div>
    </div>
</template>

<style>
    .step-icon {
        width: 4rem;
        height: 4rem;
        overflow: hidden;
        opacity: 1;
        background-color: #faf1fa;
        border-radius: var(--border-radius);
    }
</style>
